import * as React from 'react';
import { Box, Button } from '@mui/material';

import { MuiModal } from 'ui/MuiModal';
import { MuiForm } from 'lib/Form';
import { IItemContext } from '../../models/ContentModule';
import {
    ModuleSchedulingForm,
    ModuleSchedulingFormValues,
    buildModuleSchedulingFormValues,
    buildSchedule,
    defaultModuleSchedulingFormValues,
    moduleSchedulingValidationSchema,
    toMerchantISO
} from './ModuleSchedulingForm';
import { Form } from 'formik';

interface ModuleSchedulingModalProps {
    existingSchedule: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>;
    merchantTimezone: string;
    open: boolean;
    onClose: () => void;
    onSubmit: (values: Pick<IItemContext, 'visibleTo' | 'visibleFrom' | 'schedule'>) => void;
}

export const ModuleSchedulingModal = ({
    open,
    merchantTimezone,
    onClose,
    onSubmit,
    existingSchedule
}: ModuleSchedulingModalProps) => {
    const handleSubmit = React.useCallback(
        (values: ModuleSchedulingFormValues) => {
            const schedule = buildSchedule(values.days);

            onSubmit({
                schedule: schedule.length ? schedule : undefined,
                visibleFrom:
                    values.hasSpecificDates && values.startDateTime
                        ? toMerchantISO(values.startDateTime, merchantTimezone)
                        : undefined,
                visibleTo:
                    values.hasSpecificDates && values.endDateTime
                        ? toMerchantISO(values.endDateTime, merchantTimezone)
                        : undefined
            });
        },
        [onSubmit, merchantTimezone]
    );

    const formInitialValues = React.useMemo(
        () => buildModuleSchedulingFormValues(existingSchedule),
        [existingSchedule]
    );

    return (
        <MuiModal open={open} onClose={onClose} paperMaxWidth={720}>
            <MuiForm
                onSubmit={handleSubmit}
                initialValues={formInitialValues ?? defaultModuleSchedulingFormValues}
                validationSchema={moduleSchedulingValidationSchema}
                enableReinitialize
            >
                {({ submitForm, isSubmitting, isValid }) => (
                    <>
                        <Form>
                            <ModuleSchedulingForm />
                        </Form>
                        <Box display="flex" justifyContent="flex-end" pt={2} gap={1}>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={submitForm}
                                disabled={!isValid || isSubmitting}
                            >
                                Save
                            </Button>
                        </Box>
                    </>
                )}
            </MuiForm>
        </MuiModal>
    );
};
